import { isEmpty } from 'lodash/fp';

import request from '../../api/request';
import {
    PaginatedResponse,
    PaginationQueryParams,
    SurveyFilters,
    SurveyResource,
    SurveyResourceReadOnlyProps,
    SurveyResponse,
} from '../../types';

export const surveyService = {
    getAllSurveys: (filters?: PaginationQueryParams): Promise<PaginatedResponse<SurveyResource>> =>
        request.get('/surveys', filters, { timeout: 90_000 }),

    createSurvey: (
        data: Omit<SurveyResource, SurveyResourceReadOnlyProps>
    ): Promise<SurveyResource> => request.post('/surveys', data),

    getSurvey: (surveyId: number): Promise<SurveyResource> => request.get(`/surveys/${surveyId}`),

    updateSurvey: (
        surveyId: number,
        data: Omit<SurveyResource, SurveyResourceReadOnlyProps>
    ): Promise<SurveyResource> => request.put(`/surveys/${surveyId}`, data),

    deleteSurvey: (surveyId: number): Promise<boolean> => request.delete(`/surveys/${surveyId}`),

    shareSurvey: (organizationId: number, surveyId: number): Promise<SurveyResource> =>
        request.post(`/organizations/${organizationId}/surveys/${surveyId}`),

    unshareSurvey: (organizationId: number, surveyId: number): Promise<SurveyResource> =>
        request.delete(`/organizations/${organizationId}/surveys/${surveyId}`),

    getMyOrganizationSurveys: (
        filters?: Partial<SurveyFilters>
    ): Promise<PaginatedResponse<SurveyResource>> =>
        request.get('/my-organization/surveys', filters, { timeout: 90_000 }),

    getSurveyResponses: (
        surveyId: number,
        filters?: SurveyFilters
    ): Promise<PaginatedResponse<SurveyResponse>> =>
        request.get(`/surveys/${surveyId}/responses`, filters, { timeout: 90_000 }),

    submitSurveyResponse: (surveyId: number, responses?: SurveyResponse[]): Promise<boolean> =>
        request.post(`/my-organization/surveys/${surveyId}/responses`, { data: responses }),

    unshareSurveysFromOrganizations: async (organizationIds: number[]): Promise<void> => {
        if (isEmpty(organizationIds)) return;

        const organizationIdsSet = new Set(organizationIds);
        const { results: surveys } = await surveyService.getAllSurveys({
            per_page: String(1_000_000),
        });
        if (isEmpty(surveys)) return;

        const unshareRequests = surveys.flatMap((survey) =>
            survey.organizations
                .filter(({ id }) => organizationIdsSet.has(id))
                .map(({ id }) => surveyService.unshareSurvey(id, survey.id))
        );

        await Promise.all(unshareRequests);
    },
};
